import React, { useState, useEffect } from 'react';
import { Calculator, Users, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const MarketPenetrationRateCalculator = () => {
  const [currentCustomers, setCurrentCustomers] = useState(10000);
  const [totalMarketSize, setTotalMarketSize] = useState(100000);
  const [penetrationRate, setPenetrationRate] = useState(0);

  useEffect(() => {
    calculatePenetrationRate();
  }, [currentCustomers, totalMarketSize]);

  const calculatePenetrationRate = () => {
    const rate = (currentCustomers / totalMarketSize) * 100;
    setPenetrationRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/market-penetration-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Market Penetration Rate Calculator: Measure Market Coverage | Free Tool</title>
        <meta name="description" content="Calculate your Market Penetration Rate instantly. Compare with industry benchmarks, get optimization tips, and improve market coverage." />
        <meta name="keywords" content="market penetration calculator, market coverage metrics, customer reach, market analysis, business growth" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Market Penetration Calculator: Optimize Market Coverage" />
        <meta property="og:description" content="Calculate your Market Penetration Rate instantly. Get actionable insights to improve market coverage and customer reach." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Market Penetration Calculator: Optimize Market Coverage" />
        <meta property="twitter:description" content="Calculate your Market Penetration Rate instantly. Get actionable insights to improve market coverage and customer reach." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Market Penetration Rate Calculator",
            "description": "Free calculator to measure and optimize market penetration and customer reach",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Penetration Rate",
              "Industry Benchmarks",
              "Market Analysis",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Market Penetration Rate",
              "description": "The percentage of potential customers currently using your product or service."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Market Analysts, Business Strategists, Product Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Market Penetration Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter current customers"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total market size"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your penetration rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Market Penetration Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Customers</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={currentCustomers}
                  onChange={(e) => setCurrentCustomers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Market Size</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalMarketSize}
                  onChange={(e) => setTotalMarketSize(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="100000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {penetrationRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Market Penetration Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Market Penetration Rate is {penetrationRate}%. This means that your product or service is currently being used by {penetrationRate}% of the total available market.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Penetration Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Technology Products: 15-30%</li>
          <li>Consumer Goods: 40-60%</li>
          <li>B2B Services: 10-25%</li>
          <li>Luxury Products: 5-15%</li>
          <li>Essential Services: 70-90%</li>
          <li>Niche Markets: 30-50%</li>
          <li>Emerging Markets: 5-20%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Market Penetration Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Develop targeted campaigns</li>
          <li>Optimize pricing strategy</li>
          <li>Expand distribution</li>
          <li>Improve product features</li>
          <li>Implement loyalty programs</li>
          <li>Conduct market research</li>
          <li>Form strategic partnerships</li>
          <li>Enter new segments</li>
          <li>Enhance brand awareness</li>
          <li>Leverage digital channels</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Success Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Product-Market Fit</li>
          <li>Value Proposition</li>
          <li>Market Accessibility</li>
          <li>Customer Experience</li>
          <li>Brand Recognition</li>
          <li>Competitive Pricing</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Market expansion: +15-20% penetration</li>
          <li>Price optimization: +10-15% penetration</li>
          <li>Product improvement: +8-12% penetration</li>
          <li>Channel development: +12-18% penetration</li>
          <li>Brand building: +5-10% penetration</li>
        </ul>
      </div>
    </div>
  );
};

export default MarketPenetrationRateCalculator;
